import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert, Box, Button, Chip, CircularProgress, DialogContent, DialogTitle,
  IconButton, OutlinedInput, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppDialog from '../../../shared/components/AppDialog';
import EmptyState from '../../../shared/components/EmptyState';
import { dataTableSx, HeadLabelCell } from '../../../shared/components/TableHeader';
import useElectricitySupplierCatalog from '../hooks/useElectricitySupplierCatalog';
import ElectricitySupplierFormPanel from './ElectricitySupplierFormPanel';

const ROWS_PER_PAGE = 10;

export default function ElectricitySupplierManagerDialog({
  open, onClose, onCatalogChanged,
}) {
  const { t } = useTranslation();
  const catalog = useElectricitySupplierCatalog({ enabled: open });
  const [editing, setEditing] = useState(undefined);
  const [page, setPage] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const isFormOpen = editing !== undefined;

  useEffect(() => {
    if (open) {
      setEditing(undefined);
      setPage(0);
      setFeedback(null);
    }
  }, [open]);
  useEffect(() => setPage(0), [catalog.search]);

  const saveSupplier = async (payload) => {
    const saved = await catalog.saveSupplier(editing || null, payload);
    setFeedback(t(editing
      ? 'office.supplierCatalog.updated'
      : 'office.supplierCatalog.created', { registration: saved.registrationNo }));
    setEditing(undefined);
    await onCatalogChanged?.();
  };
  const close = () => {
    if (!catalog.saving) onClose();
  };
  const pageRows = catalog.suppliers.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  return (
    <AppDialog open={open} onClose={catalog.saving ? undefined : close} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t(isFormOpen
          ? (editing ? 'office.supplierCatalog.editTitle' : 'office.supplierCatalog.addTitle')
          : 'office.supplierCatalog.title')}
        <IconButton
          onClick={close}
          disabled={catalog.saving}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isFormOpen ? (
          <ElectricitySupplierFormPanel
            supplier={editing || null}
            saving={catalog.saving}
            onCancel={() => setEditing(undefined)}
            onSubmit={saveSupplier}
          />
        ) : (
          <Stack spacing={2}>
            {feedback && <Alert severity="success">{feedback}</Alert>}
            {catalog.loadError && (
              <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={catalog.reload}>{t('common.retry')}</Button>}
              >
                {t('office.supplierCatalog.loadFailed')}
              </Alert>
            )}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="space-between"
            >
              <OutlinedInput
                size="small"
                value={catalog.search}
                onChange={event => catalog.setSearch(event.target.value)}
                startAdornment={<SearchRoundedIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                placeholder={t('office.supplierCatalog.search')}
                inputProps={{ 'aria-label': t('office.supplierCatalog.search') }}
                sx={{ minWidth: { sm: 320 } }}
              />
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => { setEditing(null); setFeedback(null); }}
                disabled={catalog.loading || Boolean(catalog.loadError)}
              >
                {t('office.supplierCatalog.add')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('office.supplierCatalog.count', {
                shown: catalog.suppliers.length,
                total: catalog.total,
              })}
            </Typography>
            {catalog.loading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}>
                <CircularProgress size={28} aria-label={t('office.supplierCatalog.loading')} />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 440, overflow: 'auto' }}>
                  <Table stickyHeader size="small" sx={dataTableSx}>
                    <TableHead>
                      <TableRow>
                        <HeadLabelCell width={80}>{t('common.actions')}</HeadLabelCell>
                        <HeadLabelCell width={110}>{t('office.supplierCatalog.registrationNo')}</HeadLabelCell>
                        <HeadLabelCell>{t('office.supplierCatalog.name')}</HeadLabelCell>
                        <HeadLabelCell width={120}>{t('common.status')}</HeadLabelCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map(supplier => (
                        <TableRow key={supplier.id} hover>
                          <TableCell>
                            <Tooltip title={t('common.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => { setEditing(supplier); setFeedback(null); }}
                                aria-label={t('office.supplierCatalog.editSupplier', {
                                  registration: supplier.registrationNo,
                                })}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={750} sx={{ fontFamily: 'monospace' }}>
                              {supplier.registrationNo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={650}>{supplier.nameJa}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(supplier.isActive ? 'common.active' : 'common.inactive')}
                              color={supplier.isActive ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {pageRows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} sx={{ border: 0 }}>
                            <EmptyState
                              title={t('common.noResults')}
                              description={t('common.noResultsHint')}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={catalog.suppliers.length}
                  page={page}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[]}
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </AppDialog>
  );
}
